import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { CustomWarmupPlan, MailboxType } from "@/types/mailbox.types";
import { TrendingUp, Shuffle, Minus } from "lucide-react";

const STRATEGY_CONFIG = {
  progressive: {
    label: "Progressive",
    short: "Prog",
    icon: TrendingUp,
    className:
      "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    dotClassName: "bg-emerald-500",
  },
  random: {
    label: "Random",
    short: "Rand",
    icon: Shuffle,
    className:
      "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    dotClassName: "bg-amber-500",
  },
  flat: {
    label: "Flat",
    short: "Flat",
    icon: Minus,
    className:
      "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30",
    dotClassName: "bg-violet-500",
  },
} as const;

export function getStrategyConfig(strategy: string | undefined) {
  if (strategy && strategy in STRATEGY_CONFIG) {
    return STRATEGY_CONFIG[strategy as keyof typeof STRATEGY_CONFIG];
  }
  return {
    label: "Default",
    short: "—",
    icon: Minus,
    className:
      "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30",
    dotClassName: "bg-slate-500",
  };
}

/** Format the main value shown next to the strategy pill (range for random, constant for flat, today/max for progressive). */
function formatDisplayValue(
  plan: CustomWarmupPlan | null | undefined,
  fallbackDailyLimit: number | undefined
): string {
  const strategy = plan?.warmupStrategy;

  if (strategy === "random") {
    const min = plan?.randomMinLimit;
    const max = plan?.randomMaxLimit;
    if (min != null && max != null) return `${min}–${max}`;
    return "—";
  }

  if (strategy === "flat") {
    const constant = plan?.flatConstantEmails;
    if (constant != null) return String(constant);
    return "—";
  }

  // progressive or default
  const today = plan?.currentCounter ?? fallbackDailyLimit;
  if (today == null || today === undefined) return "—";
  const max = plan?.maxDailyLimit ?? plan?.progressiveMaxLimit;
  if (max != null && max > 0) return `${today}/${max}`;
  return String(today);
}

export function buildTooltipText(data: MailboxType): string {
  const plan = data.customWarmupPlan;
  const strategy = plan?.warmupStrategy ?? "default";
  const config = getStrategyConfig(strategy);
  const today = plan?.currentCounter ?? data.currentDailyLimit;

  const lines: string[] = [];
  lines.push(`Strategy: ${config.label}`);

  if (strategy === "random") {
    if (plan?.randomMinLimit != null && plan?.randomMaxLimit != null) {
      lines.push(`Range: ${plan.randomMinLimit}–${plan.randomMaxLimit} per day`);
      lines.push(`Max: ${plan.randomMaxLimit}`);
    }
  } else if (strategy === "flat") {
    if (plan?.flatConstantEmails != null) {
      lines.push(`Fixed: ${plan.flatConstantEmails} emails/day`);
    }
    if (today != null) lines.push(`Today: ${today}`);
  } else {
    // progressive or default
    if (today != null) lines.push(`Today: ${today}`);
    if (plan?.maxDailyLimit != null && plan.maxDailyLimit > 0)
      lines.push(`Max daily: ${plan.maxDailyLimit}`);
    if (strategy === "progressive" && plan?.progressiveStartingVolume != null && plan?.progressiveMaxLimit != null) {
      lines.push(`Ramp: ${plan.progressiveStartingVolume} → ${plan.progressiveMaxLimit} (+${plan.progressiveIncreaseBy ?? "?"}/day)`);
    }
  }
  return lines.join(" · ");
}

interface WarmupStrategyCellProps {
  data: MailboxType;
}

export function WarmupStrategyCell({ data }: WarmupStrategyCellProps) {
  const plan = data.customWarmupPlan;
  const strategy = plan?.warmupStrategy;
  const config = getStrategyConfig(strategy);
  const Icon = config.icon;
  const valueDisplay = formatDisplayValue(plan, data.currentDailyLimit);
  const tooltipText = buildTooltipText(data);

  const hasWarmup = data.isWarmupEnabled;
  if (!hasWarmup) {
    return (
      <div className="flex items-center h-full text-muted-foreground text-sm">
        <span className="italic">Off</span>
      </div>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center h-full gap-2 min-w-0 cursor-default">
          <div
            className={cn(
              "flex items-center gap-1.5 shrink-0 rounded-md border px-2 py-0.5 text-xs font-medium",
              config.className
            )}
          >
            <span className={cn("size-1.5 rounded-full shrink-0", config.dotClassName)} />
            <Icon className="size-3 shrink-0" />
            <span className="truncate">{config.short}</span>
          </div>
          <span className="text-sm font-semibold tabular-nums text-foreground truncate">
            {valueDisplay}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[280px]">
        <p className="text-xs">{tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  );
}
